import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  warehouseService,
  type CreateWarehousePayload,
  type UpdateWarehousePayload,
  type Warehouse,
  type WarehouseListQuery,
} from '@/services/warehouse.service';

const KEY_ALL = ['warehouses'] as const;
const keyList = (q: WarehouseListQuery) => [...KEY_ALL, 'list', q] as const;
const keyDetail = (id: string) => [...KEY_ALL, 'detail', id] as const;

// Rà soát cache FE (2026-09-07) — nhiều trang KHÔNG dùng useWarehouses() ở trên mà tự viết
// useQuery với literal key riêng để fetch CÙNG danh sách kho (warehouseService.list) —
// SalesInvoices (create/derived-document-edit/SalesReturnDrawer/warehouse-voucher-listing),
// Products (detail/ProductForm), Services (detail/ServiceForm), AccountingReports/inventory-summary.
// invalidateQueries({ queryKey: KEY_ALL }) chỉ khớp key bắt đầu bằng 'warehouses' nên KHÔNG đụng
// tới các key rời rạc này — tạo/sửa/xoá kho ở trang Quản lý kho không làm các dropdown/trang trên
// tự cập nhật nếu đang mở song song. Liệt kê tường minh toàn bộ key rời rạc đã biết để 3 mutation
// dưới cùng invalidate — không đổi lại các trang tiêu thụ (giữ nguyên hành vi/shape hiện có, chỉ vá
// đường truyền tin cache).
const STALE_LEGACY_WAREHOUSE_LIST_KEYS = [
  ['warehouses-list'],
  ['warehouses-for-product-detail'],
  ['warehouses-product-form'],
  ['warehouses-for-service-detail'],
  ['warehouses-service-form'],
  ['inventory-warehouses'],
] as const;

function invalidateAllWarehouseLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: KEY_ALL });
  STALE_LEGACY_WAREHOUSE_LIST_KEYS.forEach((key) => qc.invalidateQueries({ queryKey: key }));
}

export function useWarehouses(
  query: WarehouseListQuery = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: keyList(query),
    queryFn: () => warehouseService.list(query),
    placeholderData: (prev) => prev,
    // Tuỳ chọn `enabled` — mặc định `undefined` (TanStack Query coi như
    // `true`) nên MỌI lời gọi cũ không truyền `options` giữ nguyên hành vi
    // (luôn fetch). Thêm để `ImportOrderModal` chỉ fetch danh sách kho khi
    // modal đang mở, thay vì fetch vô điều kiện mỗi lần trang /orders render.
    enabled: options.enabled,
  });
}

export function useWarehouseDetail(
  id: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: keyDetail(id ?? ''),
    queryFn: () => warehouseService.get(id!),
    enabled: Boolean(id) && options.enabled !== false,
  });
}

export function useCreateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWarehousePayload) => warehouseService.create(body),
    onSuccess: () => invalidateAllWarehouseLists(qc),
  });
}

export function useUpdateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateWarehousePayload }) =>
      warehouseService.update(id, body),
    onSuccess: () => invalidateAllWarehouseLists(qc),
  });
}

export function useDeleteWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => warehouseService.remove(id),
    onSuccess: () => invalidateAllWarehouseLists(qc),
  });
}

export type { Warehouse };
